import React, { useState } from 'react';
import { Plus, Edit2, X } from 'lucide-react';
import {
  deleteRestaurantRevenueSource,
  fetchRevenueSourcesByRestaurantIds,
  FrontendRestaurantRevenueSource,
} from '../../services/restaurant/restaurantRevenueSourceService';
import { SaveRevenueSourceModal, Mode } from './SaveRevenueSourceModal';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { RevenueSource, getRevenueSourceDisplayName } from '../../types/RevenueSource';
import { getRevenueSourceTypeDisplayName } from '../../types/RevenueSourceType';
import { useTranslation } from 'react-i18next';

interface Props {
  restaurantId: string;
}

export const RestaurantRevenueSourcesList: React.FC<Props> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const store = useRestaurantStore();
  const revenueSources = store.restaurantRevenueSources[restaurantId] || [];
  const setRevenueSources = store.setRestaurantRevenueSources;

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('create');
  const [selected, setSelected] = useState<FrontendRestaurantRevenueSource | null>(null);
  const [deletingRevenueSource, setDeletingRevenueSource] = useState<FrontendRestaurantRevenueSource | null>(null)

  const refresh = async () => {
    const list = await fetchRevenueSourcesByRestaurantIds([restaurantId]);
    setRevenueSources(restaurantId, list);
  };

  const openCreate = () => {
    setMode('create');
    setSelected(null);
    setModalOpen(true);
  };

  const openEdit = (rs: FrontendRestaurantRevenueSource) => {
    setMode('edit');
    setSelected(rs);
    setModalOpen(true);
  };

  const handleDelete = (revenueSource: FrontendRestaurantRevenueSource) => {
    setDeletingRevenueSource(revenueSource)
  }

  const confirmDelete = async () => {
    if (!deletingRevenueSource) return

    await deleteRestaurantRevenueSource(deletingRevenueSource.id)
    await refresh()
    setDeletingRevenueSource(null)
  }

  return (
    <>
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide">{t('restaurantRevenueSources.revenueSources')}</h4>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 text-xs font-medium transition border border-zinc-200 dark:border-zinc-700"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('restaurantRevenueSources.add')}
          </button>
        </div>
      </div>

      {revenueSources.length > 0 ? (
        <ul className="space-y-2">
          {revenueSources.map(rs => (
            <li
              key={rs.id}
              className="p-4 rounded-xl flex items-start justify-between bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-zinc-900 dark:text-zinc-50 mb-2">
                  {getRevenueSourceDisplayName(rs.revenueSourceName as RevenueSource)}
                </p>
                <div className="space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
                  <p className="truncate">
                    <span className="font-medium">{t('restaurantRevenueSources.type')}:</span> {getRevenueSourceTypeDisplayName(rs.revenueSourceType)}
                  </p>
                  <p className="truncate">
                    <span className="font-medium">{t('restaurantRevenueSources.restaurant')}:</span> {rs.restaurantName}
                  </p>
                  {rs.relevantSections.length > 0 && (
                    <p className="truncate">
                      <span className="font-medium">{t('restaurantRevenueSources.sections')}:</span> {rs.relevantSections.join(', ')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  onClick={() => openEdit(rs)}
                  className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
                  aria-label={t('restaurantRevenueSources.edit')}
                >
                  <Edit2 className="w-4 h-4 text-zinc-700 dark:text-zinc-300" />
                </button>
                <button
                  onClick={() => handleDelete(rs)}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                  aria-label={t('restaurantRevenueSources.delete')}
                >
                  <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
          {t('restaurantRevenueSources.noRevenueSources')}
        </p>
      )}

      <SaveRevenueSourceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={mode}
        restaurantId={restaurantId}
        initial={selected || undefined}
        onSaved={refresh}
      />

      {/* Delete Confirmation Modal */}
      {deletingRevenueSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={() => setDeletingRevenueSource(null)}>
          <div
            className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50">
                  {t('restaurantRevenueSources.deleteRevenueSource')}
                </h2>
                <button
                  onClick={() => setDeletingRevenueSource(null)}
                  className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                  aria-label={t('restaurantRevenueSources.close')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-zinc-600 dark:text-zinc-400">
                  {t('restaurantRevenueSources.deleteRevenueSourceConfirmation', { name: getRevenueSourceDisplayName(deletingRevenueSource.revenueSourceName as RevenueSource) })}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingRevenueSource(null)}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  {t('restaurantRevenueSources.cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-700 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-800 transition"
                >
                  {t('restaurantRevenueSources.deleteRevenueSourceButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};